// src/constants/vehicleTypes.js
// Centralización de todos los tipos de vehículos y funciones relacionadas

import i18n from '../i18n';

// Tipos de vehículos válidos según el backend
export const VEHICLE_TYPES = {
  MOTORCYCLE: 'motorcycle',
  CAR: 'car',
  ATV: 'atv',
  BUGGY: 'buggy',
  GO_KART: 'go_kart'
};

// Array de todos los tipos de vehículos para uso en componentes
export const ALL_VEHICLE_TYPES = Object.values(VEHICLE_TYPES);

// Orden de prioridad para mostrar tipos (mayor número = mayor prioridad)
export const VEHICLE_TYPE_PRIORITIES = {
  [VEHICLE_TYPES.MOTORCYCLE]: 7,
  [VEHICLE_TYPES.CAR]: 6,
  [VEHICLE_TYPES.ATV]: 5,
  [VEHICLE_TYPES.BUGGY]: 3,
  [VEHICLE_TYPES.GO_KART]: 2
};

// Funciones helper

/**
 * Obtiene la etiqueta localizada para un tipo de vehículo
 * @param {string} vehicleType - El tipo de vehículo
 * @returns {string} La etiqueta localizada
 */
export const getVehicleTypeLabel = (vehicleType) => {
  if (!vehicleType) return 'Tipo desconocido';
  return i18n.t(`vehicleTypes:allowedVehicleTypes.${vehicleType}`, vehicleType);
};

/**
 * Función para obtener la etiqueta traducida de un tipo de vehículo
 * @param {string} vehicleType - El tipo de vehículo
 * @returns {string} La etiqueta traducida
 */
export const getTranslatedVehicleTypeLabel = (vehicleType) => {
  if (!vehicleType) return i18n.t('vehicleTypes:basic.unknown', 'Tipo desconocido');
  return i18n.t(`vehicleTypes:allowedVehicleTypes.${vehicleType}`, vehicleType);
};

/**
 * Obtiene la prioridad de un tipo de vehículo para ordenamiento
 * @param {string} vehicleType - El tipo de vehículo
 * @returns {number} La prioridad del tipo
 */
export const getVehicleTypePriority = (vehicleType) => {
  return VEHICLE_TYPE_PRIORITIES[vehicleType] || 0;
};

/**
 * Obtiene todos los tipos de vehículos válidos como array
 * @returns {string[]} Array con todos los tipos válidos
 */
export const getAllVehicleTypes = () => {
  return ALL_VEHICLE_TYPES;
};

/**
 * Valida si un tipo de vehículo es válido
 * @param {string} vehicleType - El tipo a validar
 * @returns {boolean} True si el tipo es válido
 */
export const isValidVehicleType = (vehicleType) => {
  return ALL_VEHICLE_TYPES.includes(vehicleType);
};

/**
 * Ordena un array de tipos de vehículos por prioridad
 * @param {string[]} vehicleTypes - Array de tipos de vehículos
 * @returns {string[]} Array ordenado por prioridad
 */
export const sortVehicleTypesByPriority = (vehicleTypes) => {
  return vehicleTypes.sort((a, b) => getVehicleTypePriority(b) - getVehicleTypePriority(a));
};

/**
 * Hook personalizado para usar traducciones de tipos de vehículos (para componentes React)
 * @returns {object} Objeto con funciones de traducción
 */
export const useVehicleTypeTranslations = () => {
  return {
    getVehicleTypeLabel: (vehicleType) => {
      if (!vehicleType) return 'Tipo desconocido';
      return i18n.t(`vehicleTypes:allowedVehicleTypes.${vehicleType}`, 'Tipo desconocido');
    },
    getAllLabel: () => {
      return i18n.t('vehicleTypes:basic.all', 'Todos');
    }
  };
};

/**
 * Obtiene los tipos de vehículos ordenados con sus etiquetas traducidas
 * @returns {Array} Array de objetos con value y label
 */
export const getVehicleTypesWithLabels = () => {
  return sortVehicleTypesByPriority(ALL_VEHICLE_TYPES).map(type => ({
    value: type,
    label: getVehicleTypeLabel(type)
  }));
};
